import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getPaymentCard } from '../../../../actions/paymentActions';
import {
	submitInternationalShipment,
	submitInternationalShipmentFailed,
} from '../../../../actions/registerShipmentActions';
import { UPDATE_TOTAL_PRICE } from '../../../../constants/actions/registerShipmentFormActions';
import { formatCurrency } from '../../../../helpers/formatting/formatting';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import usePostalServices from '../../../../hooks/usePostalServices';
import useRecipients from '../../../../hooks/useRecipients';
import IPostService from '../../../../interfaces/forms/postService';
import IPaymentReducer from '../../../../interfaces/reducers/paymentReducer';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IReturn } from '../../../../interfaces/returns/return';
import { IStore } from '../../../../reducers';
import { IRegisterShipmentReducer } from '../../../../reducers/registerShipmentReducer';
import Button from '../../../base/button';
import { StyledCheckbox } from '../../../base/forms/checkbox';
import { StyledCheckboxLabel, StyledCheckboxWrapper } from '../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../base/forms/formikSelect';
import { Input, StyledFormikTextArea } from '../../../base/forms/input';
import { Label } from '../../../base/forms/label';
import { Column, Columns } from '../../../base/grid';
import { Flex } from '../../../base/misc/misc';
import { A, P } from '../../../base/typography';
import { WizardPageProps } from '../../../wizard/wizard';
import { computeLineValue, isItemWeightRequired } from '../stepExport/stepExportForm/helpers';
import { IStepExportFormInfo } from '../stepExport/stepExport.schema';
import { IStepInfoFormInfo } from '../stepInfo/stepInfo.schema';
import { getReturnSchema } from './stepConfirmation.schema';
import * as StyledStepConfirmation from './stepConfirmation.styles';
import { HelperText } from './stepConfirmation.styles';

interface Props extends WizardPageProps {
	isReturn: boolean;
}

const StepConfirmation: FC<Props> = ({ onSubmit, onPrevious, isReturn = false }) => {
	const dispatch = useDispatch<any>();
	const registerShipmentReducer: IRegisterShipmentReducer = useSelector<IStore, IRegisterShipmentReducer>(
		store => store.registerShipmentReducer
	);
	const infoFormInfo: IStepInfoFormInfo = registerShipmentReducer.stepInfoFormInfo;
	const exportFormInfo: IStepExportFormInfo = registerShipmentReducer.stepExportFormInfo;
	const {
		submissionLoading,
		submissionFulfilled,
		submissionFailed,
		submissionErrorMessage,
		registrationKey,
		shipmentId,
		customerId,
	} = registerShipmentReducer;
	const shipmentFormInfo = registerShipmentReducer.stepShipmentFormInfo;
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);

	const isCardExpired = (expMonth: number, expYear: number): boolean => {
		const today = new Date();
		const currentMonth = today.getMonth() + 1; // JS months are 0-indexed
		const currentYear = today.getFullYear();
		const fullYear = expYear < 100 ? 2000 + expYear : expYear;

		return fullYear < currentYear || (fullYear === currentYear && expMonth < currentMonth);
	};

	const { isLoggedIn, userProfile } = userProfileReducer;
	const { recipients } = useRecipients(userProfile);
	const domestic = infoFormInfo.country.value === 'IS';
	const showItemWeight = isItemWeightRequired(
		infoFormInfo.country?.value,
		exportFormInfo.packageType?.value,
		shipmentFormInfo.serviceOption === 'InternationalLetter'
	);
	const lineWeights = exportFormInfo.contents.map(contentLine =>
		showItemWeight && contentLine.weight?.trim()
			? computeLineValue(contentLine.weight, contentLine.amountInPackage)
			: ''
	);
	const { t } = useTranslation('registerShipment');
	const history = useHistory();
	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const { paymentCard } = paymentReducer;
	const paymentCardExists = paymentCard !== null; /*&& userProfile.paymentCardId*/
	const [approvalCard, setApprovalCard] = useState(false);
	const needsCardApproval = isLoggedIn && paymentCardExists;

	const returnOptions = [
		{ value: 'gift-swap', label: t('step-conformation.return-options.gift-swap') },
		{ value: 'gift-credit', label: t('step-conformation.return-options.gift-credit') },
		{ value: 'return-swap', label: t('step-conformation.return-options.return-swap') },
		{ value: 'return-credit', label: t('step-conformation.return-options.return-credit') },
	];

	const fromPostalCode = useMemo(
		() =>
			infoFormInfo.senderPostCode?.value
				? {
						value: infoFormInfo.senderPostCode.value,
						label: '',
				  }
				: null,
		[infoFormInfo.senderPostCode]
	);

	const toPostalCode = useMemo(
		() =>
			infoFormInfo.receiverPostCode?.value
				? {
						value: infoFormInfo.receiverPostCode.value,
						label: '',
				  }
				: null,
		[infoFormInfo.receiverPostCode]
	);

	const {
		services,
		servicesLoading,
	}: {
		services: IPostService[];
		servicesLoading: boolean;
	} = usePostalServices(2, 'Parcel', infoFormInfo.country, fromPostalCode, toPostalCode, '0', '0', '0');

	useEffect(() => {
		if (services && !servicesLoading && isReturn) {
			const service = services?.find(x => x.id === 'PostOffice');
			if (service) {
				dispatch({
					type: UPDATE_TOTAL_PRICE,
					payload: { price: service.priceAmount },
				});
			}
		}
	}, [services, servicesLoading, dispatch, isReturn]);

	const onConfirmationSubmit = (returnType: IReturn | false = false) => {
		if (needsCardApproval && !approvalCard) {
			dispatch(submitInternationalShipmentFailed(t('youHaveToAccept')));
			return;
		}

		if (
			approvalCard &&
			paymentCard &&
			isCardExpired(paymentCard.expiryMonth, Number(String(paymentCard.expiryYear).slice(-2)))
		) {
			dispatch(submitInternationalShipmentFailed(t('myShipments:cardExpired')));
			return;
		}

		dispatch(
			submitInternationalShipment(
				userProfile?.userId || null,
				isLoggedIn,
				recipients,
				history,
				approvalCard,
				returnType
			)
		);
	};

	useEffect(() => {
		if (submissionFulfilled) {
			onSubmit();
		}
	}, [submissionFulfilled, registrationKey, shipmentId, customerId, onSubmit]);

	useEffect(() => {
		if (isLoggedIn && !paymentCardExists) dispatch(getPaymentCard());
	}, [isLoggedIn, dispatch, paymentCardExists]);

	return (
		<Formik
			initialValues={{ returnType: undefined, returnReason: '', returnOrderId: '', returnProduct: '' }}
			onSubmit={values => {
				if (isReturn) {
					onConfirmationSubmit({
						type: values.returnType.label,
						reason: values.returnReason,
						product: values.returnProduct,
						orderId: values.returnOrderId,
						name: infoFormInfo.senderName,
						email: infoFormInfo.senderEmail,
						phone: infoFormInfo.senderPhone,
						address: infoFormInfo.senderAddress + ', ' + infoFormInfo.senderPostCode.label,
					});
				} else onConfirmationSubmit();
			}}
			validationSchema={getReturnSchema(isReturn)}>
			{({ values, handleSubmit, setFieldValue, setFieldTouched }) => (
				<Form>
					<StyledStepConfirmation.Wrapper>
						<StyledStepConfirmation.FieldColumns>
							<Column tablet={1 / 2}>
								<Label>{t('shared.recipient')}</Label>
								<StyledStepConfirmation.FieldColumns marginBottom="6rem">
									<Column>
										{!domestic && (
											<Flex>
												<StyledStepConfirmation.Strong>
													{t('step-conformation.country')}
												</StyledStepConfirmation.Strong>
												<StyledStepConfirmation.CountryWrapper>
													<img
														src={`/dist/images/flags-small/${infoFormInfo.country.value.toLowerCase()}.png`}
														alt={`${infoFormInfo.country.label} flag`}
													/>
													<div>{infoFormInfo.country.label}</div>
												</StyledStepConfirmation.CountryWrapper>
											</Flex>
										)}
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.ssn')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.receiverSsn}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.name')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.receiverName}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.address')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.receiverAddress}</div>
										</Flex>
										{!domestic && (
											<Flex>
												<StyledStepConfirmation.Strong>
													{`${t('step-info.address-cont')}:`}
												</StyledStepConfirmation.Strong>
												<div>{infoFormInfo.receiverForeignAddressExtended}</div>
											</Flex>
										)}
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.postcode')} / ${t('step-info.place')}:`}
											</StyledStepConfirmation.Strong>
											<div>
												{domestic && infoFormInfo.receiverPostCode
													? infoFormInfo.receiverPostCode.label
													: infoFormInfo.receiverForeignPostCode}
											</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.phone')}:`}
											</StyledStepConfirmation.Strong>
											<div>
												{infoFormInfo.receiverPhone === ''
													? shipmentFormInfo.receiverPhone
													: infoFormInfo.receiverPhone}
											</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.email')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.receiverEmail}</div>
										</Flex>
										{!domestic && (
											<>
												<Flex>
													<StyledStepConfirmation.Strong>
														{`${t('step-info.addressed-to')}:`}
													</StyledStepConfirmation.Strong>
													<div>{infoFormInfo.receiverForeignShipTo}</div>
												</Flex>
												<Flex>
													<StyledStepConfirmation.Strong>
														{`${t('step-info.place')}:`}
													</StyledStepConfirmation.Strong>
													<div>{infoFormInfo.receiverForeignPlace}</div>
												</Flex>
											</>
										)}
									</Column>
								</StyledStepConfirmation.FieldColumns>
							</Column>
							<Column tablet={1 / 2}>
								<Label>{`${t('shared.sender')}:`}</Label>
								<StyledStepConfirmation.FieldColumns marginBottom="6rem">
									<Column>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.ssn')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.senderSsn}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.name')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.senderName}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.address')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.senderAddress}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.postcode')} / ${t('step-info.place')}:`}
											</StyledStepConfirmation.Strong>
											<div>
												{infoFormInfo.senderPostCode ? infoFormInfo.senderPostCode.label : ''}
											</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.phone')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.senderPhone}</div>
										</Flex>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-info.email')}:`}
											</StyledStepConfirmation.Strong>
											<div>{infoFormInfo.senderEmail}</div>
										</Flex>
									</Column>
								</StyledStepConfirmation.FieldColumns>
							</Column>
							<Column>
								<StyledStepConfirmation.SectionDivider />
							</Column>
							<Column tablet={1 / 2}>
								<Label>{`${t('step-conformation.shipment')}:`}</Label>
								<StyledStepConfirmation.FieldColumns marginBottom="6rem">
									<Column>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-conformation.type')}:`}
											</StyledStepConfirmation.Strong>
											<div>{shipmentFormInfo.serviceName}</div>
										</Flex>
										{shipmentFormInfo.serviceOptionPostbox &&
										shipmentFormInfo.serviceOption === 'DPO' ? (
											<Flex>
												<StyledStepConfirmation.Strong>
													{`${t('step-conformation.postbox')}:`}
												</StyledStepConfirmation.Strong>
												<div>{shipmentFormInfo.serviceOptionPostbox.label}</div>
											</Flex>
										) : null}
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-conformation.number-of-parcels')}:`}
											</StyledStepConfirmation.Strong>
											<div>{shipmentFormInfo.parcelCount}</div>
										</Flex>
									</Column>
								</StyledStepConfirmation.FieldColumns>
							</Column>
							<Column tablet={1 / 2}>
								{!isReturn && ( // Hide optional services when registerign a return
									<>
										<Label>{`${t('step-optional-services.step-optional-services')}:`}</Label>
										<StyledStepConfirmation.FieldColumns>
											<Column>
												{shipmentFormInfo.optionalServicesNames.map((name, index) => {
													return <Flex key={index}>{name}</Flex>;
												})}
												{shipmentFormInfo.optionalServices.length === 0 &&
													t('step-conformation.no-optional-services')}
											</Column>
										</StyledStepConfirmation.FieldColumns>
									</>
								)}
							</Column>
							<Column>
								<StyledStepConfirmation.SectionDivider />
							</Column>
							<Column>
								{!domestic && (
									<>
										<Label>{`${t('step-export.info')}:`}</Label>
										<Flex>
											<StyledStepConfirmation.Strong>
												{`${t('step-export.package')}:`}
											</StyledStepConfirmation.Strong>
											<div>{exportFormInfo.packageType.label}</div>
										</Flex>
										<Columns flexWrap="wrap">
											{exportFormInfo.contents.map((contentLine, index) => (
												<Column key={index} tablet={1 / 2} style={{ paddingTop: '2rem' }}>
													<StyledStepConfirmation.ContentCard>
														<Flex>
															<div>
																{contentLine.amountInPackage} × {contentLine.contents}
															</div>
														</Flex>
														<Flex>
															<StyledStepConfirmation.Strong>
																{`${t('step-export.countryOfOrigin')}:`}
															</StyledStepConfirmation.Strong>
															<div>{contentLine.countryOfOrigin?.label}</div>
														</Flex>
														<Flex>
															<StyledStepConfirmation.Strong>
																{`${t('step-conformation.total-value')}:`}
															</StyledStepConfirmation.Strong>
															<div>
																{contentLine.price}
																{contentLine.price && exportFormInfo.currency?.value
																	? ` ${exportFormInfo.currency.value}`
																	: ''}
															</div>
														</Flex>
														{lineWeights[index] && (
															<Flex>
																<StyledStepConfirmation.Strong>
																	{`${t('step-export.weight')}:`}
																</StyledStepConfirmation.Strong>
																<div>{`${lineWeights[index]} kg`}</div>
															</Flex>
														)}
													</StyledStepConfirmation.ContentCard>
												</Column>
											))}
										</Columns>
									</>
								)}
							</Column>
							{isReturn && (
								<>
									<Column mobile={1}>
										<P>{t('step-conformation.return-cancel-button-text')}</P>
										<Button
											onClick={e => {
												e.preventDefault();
												history.push(window.location.pathname);
											}}>
											{t('step-conformation.return-cancel-button')}
										</Button>
									</Column>
									<Column>
										<FormikFieldWrapper
											name="returnOrderId"
											languageComponent={'registerShipment'}
											label={`${t('step-conformation.return-order-id-label')} *`}>
											<HelperText>
												{t('step-conformation.return-order-id-helper-text')}
											</HelperText>
											<Input
												id="returnOrderId"
												type="text"
												name="returnOrderId"
												placeholder={t('step-conformation.return-order-id-placeholder')}
												value={values.returnOrderId}
												onChange={e =>
													setFieldValue('returnOrderId', e.target.value.replace(/\D/g, ''))
												}
												onBlur={() => setFieldTouched('returnOrderId', true)}
											/>
										</FormikFieldWrapper>
										<br />
										<FormikFieldWrapper
											name="returnProduct"
											languageComponent={'registerShipment'}
											label={t('step-conformation.return-product-label')}>
											<HelperText>{t('step-conformation.return-product-helper-text')}</HelperText>
											<StyledFormikTextArea
												type="text"
												component="textarea"
												id="returnProduct"
												name="returnProduct"
												padding="1rem"
												placeholder={t('step-conformation.return-product-placeholder')}
												value={values.returnProduct}
											/>
										</FormikFieldWrapper>
										<br />
										<FormikFieldWrapper
											name="returnType"
											languageComponent={'registerShipment'}
											label={`${t('step-conformation.return-type-label')} *`}>
											<FormikSelect
												name="returnType"
												options={returnOptions}
												placeholder={t('step-conformation.return-type-placeholder')}
												id={'returnType'}
												onChange={setFieldValue}
												onBlur={setFieldTouched}
												value={values.returnType}
											/>
										</FormikFieldWrapper>
										<br />
										<FormikFieldWrapper
											name="returnReason"
											languageComponent={'registerShipment'}
											label={
												values.returnType?.value?.includes('swap')
													? t('step-conformation.return-reason-swap-label') + ' *'
													: t('step-conformation.return-reason-label')
											}>
											{values.returnType?.value?.includes('swap') && (
												<HelperText>
													{t('step-conformation.return-reason-helper-text')}
												</HelperText>
											)}
											<StyledFormikTextArea
												type="text"
												component="textarea"
												id="returnReason"
												name="returnReason"
												padding="1rem"
												placeholder={
													values.returnType?.value?.includes('swap')
														? t('step-conformation.return-reason-swap-placeholder')
														: t('step-conformation.return-reason-placeholder')
												}
												value={values.returnReason}
											/>
										</FormikFieldWrapper>
									</Column>
								</>
							)}
							<Column>
								<Flex>
									<StyledStepConfirmation.Strong>
										{`${t('step-conformation.price')}:`}
									</StyledStepConfirmation.Strong>
									<div>
										{formatCurrency(Number(registerShipmentReducer.totalPrice)) +
											(getCurrentLangPretty() === 'is' ? ' kr.' : ' ISK')}
									</div>
								</Flex>
							</Column>
							<Column>
								{needsCardApproval && (
									<StyledCheckboxWrapper>
										<input
											name="cardApproval"
											id="cardApproval"
											type="checkbox"
											required
											checked={approvalCard}
											onChange={e => setApprovalCard(e.target.checked)}
											// you can visually hide this via CSS if needed
										/>
										<StyledCheckboxLabel htmlFor="cardApproval">
											<StyledCheckbox checked={approvalCard} />
											<p>{t('step-conformation.approve-card-charge')}</p>
										</StyledCheckboxLabel>
									</StyledCheckboxWrapper>
								)}
								{isLoggedIn && !paymentCardExists && (
									<>
										<StyledStepConfirmation.RedP>
											<Trans
												t={t as TFunction}
												i18nKey={'step-conformation.attention-card'}
												components={[
													<A
														href={
															getCurrentLangPretty() === 'is'
																? '/minar-sidur/stillingar/'
																: '/en/my-account/settings/'
														}
													/>,
												]}
											/>
										</StyledStepConfirmation.RedP>
										<P>{t('step-conformation.post-office-option')}</P>
									</>
								)}
								{submissionFailed && (
									<StyledStepConfirmation.SubmissionFailed>
										{submissionErrorMessage || t('step-conformation.error')}
									</StyledStepConfirmation.SubmissionFailed>
								)}
							</Column>
						</StyledStepConfirmation.FieldColumns>
					</StyledStepConfirmation.Wrapper>
					<StyledStepConfirmation.ButtonWrapper>
						{!isReturn ? (
							<Button wide onClick={onPrevious}>
								{t('shared.previous-step')}
							</Button>
						) : (
							<span></span>
						)}
						<Button
							wide
							primary
							disable={submissionLoading || (needsCardApproval && !approvalCard)}
							isLoading={submissionLoading}
							onClick={handleSubmit}>
							{t('shared.conformation')}
						</Button>
					</StyledStepConfirmation.ButtonWrapper>
				</Form>
			)}
		</Formik>
	);
};

export default StepConfirmation;
